import { showErrToast } from "../Toasts";
import Button from "../../models/Button";
import { ValueLevels } from "../../models/KnxInstanceData";

import { PanelCell, PanelLayout, PannelCellLayouts } from "./PanelLayoutTypes";
import Device from "../../models/Device";
import { PanelConfig, PanelConfigs, ToPropertyResult } from "./PanelConfig";
import { isUiModeProductAdmin } from "../../uiMode";
import DebugOutput from "../../DebugOutput";

export default class PanelUtils {
  static debugOutput: DebugOutput | undefined;

  static outputError(source: string, ...args: any) {
    if (isUiModeProductAdmin()) {
      DebugOutput.outputError(source, ...args);
    }
  }

  static getKnxTableDatas(panel: Button, panelMappingConfig: PanelConfig, pageIndex: number): any[][] | undefined {
    if (panelMappingConfig?.tableId) {
      const pageInstanceId = panelMappingConfig?.pageInstanceId?.(pageIndex) || "";
      const tableInfo = panel.knxInstanceData.getTableInfo(panelMappingConfig.tableId, pageInstanceId);
      const tableInfoCellDatas = tableInfo?.cellDatas;
      return tableInfoCellDatas;
    }

    return undefined;
  }

  static applyCellToFuctionNumberProperty(panel: Button, panelConfig: PanelConfig, newCell: PanelCell) {
    try {
      const buttonChannel = panel.buttonChannels.getItemByTag(newCell.functionTag);
      const funcNumber = buttonChannel ? parseInt(buttonChannel.tag.substring(1)) : 0;
      const knxTableCellDatas = PanelUtils.getKnxTableDatas(panel, panelConfig, newCell.page);

      let propertyInfo: ToPropertyResult | undefined;
      if (knxTableCellDatas) {
        if (newCell.row !== undefined && newCell.column !== undefined) {
          const tableInfoCellData = knxTableCellDatas?.[newCell.row + 1]?.[newCell.column + 1];
          if (tableInfoCellData) {
            propertyInfo = {
              id: tableInfoCellData.id,
              instanceId: tableInfoCellData.instanceId,
            };

            const propertyInfo2 = panelConfig?.cellToFunctionNumberProperty?.({
              func: buttonChannel,
              funcNumber: funcNumber,
              idx: newCell.idx,
              page: newCell.page,
              row: newCell.row,
              column: newCell.column,
            });

            if (panelConfig?.cellEmptyValue && newCell.functionTag == "") {
              propertyInfo.value = panelConfig.cellEmptyValue;
            } else {
              if (propertyInfo2?.value != undefined) {
                propertyInfo.value = propertyInfo2.value;
              } else {
                propertyInfo.value = funcNumber.toString();
              }
            }
          } else {
            console.log("no table info cell found for row", newCell.row, "and column", newCell.column);
            return;
          }
        } else {
          console.log("no row or column found for cell", newCell);
          return;
        }
      } else {
        try {
          propertyInfo = panelConfig?.cellToFunctionNumberProperty?.({
            func: buttonChannel,
            funcNumber: funcNumber,
            idx: newCell.idx,
            page: newCell.page,
            row: newCell.row,
            column: newCell.column,
          });
        } catch (ex) {
          PanelUtils.outputError("error in cellToFunctionNumberProperty", ex);
        }
      }

      if (propertyInfo && !propertyInfo?.instanceId) {
        const pageInstanceId = panelConfig?.pageInstanceId?.(newCell.page) || "";
        propertyInfo.instanceId = pageInstanceId;
      }

      if (propertyInfo) {
        const lastValue = panel.knxInstanceData.getValue(propertyInfo.id, propertyInfo.instanceId, true);

        if (lastValue == undefined) {
          PanelUtils.outputError("Property not existing: " + propertyInfo.id);
        }

        if (lastValue != propertyInfo.value) {
          panel.knxInstanceData.setValue(
            ValueLevels.estadaUi,
            propertyInfo.value,
            propertyInfo.id,
            propertyInfo.instanceId || "",
            "",
          );

          PanelUtils.markAsManagedByEstada(panel, propertyInfo.id, propertyInfo.instanceId || "");

          panel.reEvalKnxMetadataNeeded = true;

          panel.knxInstanceData.raiseOnChange();
        }
      }
    } catch (ex) {
      console.error(ex);
    }
  }

  static insertCell(panel: Button, cell: PanelCell) {
    const panelLayout = panel.panelCells;
    if (panelLayout) {
      PanelUtils.removeCell(panel, cell);
      panelLayout.cells.push(cell);
    }
  }

  static removeCell(panel: Button, cell: PanelCell) {
    const panelLayout = panel.panelCells;
    if (panelLayout) {
      if (cell.idx) {
        const index = panelLayout.cells.findIndex((c) => c.idx === cell.idx && c.page === cell.page);
        if (index != -1) {
          panelLayout.cells.splice(index, 1);
        }
      } else {
        const index = panelLayout.cells.findIndex(
          (c) => c.column === cell.column && c.row === cell.row && c.page === cell.page,
        );
        if (index != -1) {
          panelLayout.cells.splice(index, 1);
        }
      }
    }
  }

  static getRows(panel: Button, panelMappingConfig: PanelConfig, pageIndex: number): number {
    const knxTableCellDatas = PanelUtils.getKnxTableDatas(panel, panelMappingConfig, pageIndex);
    if (knxTableCellDatas) {
      let rowCount = knxTableCellDatas?.length || 1;
      //
      // Im KnxParameterViewer entsteht eine zusätzlicher Row für die Tabelle
      rowCount--;
      return rowCount;
    } else {
      if (panelMappingConfig.rows && typeof panelMappingConfig.rows === "function") {
        return panelMappingConfig.rows(pageIndex);
      }
      return panelMappingConfig.rows || 0;
    }
  }

  static getColumns(panel: Button, panelMappingConfig: PanelConfig, pageIndex: number): number {
    const knxTableCellDatas = PanelUtils.getKnxTableDatas(panel, panelMappingConfig, pageIndex);
    if (knxTableCellDatas) {
      let columnCount = knxTableCellDatas?.[1]?.length || 1;
      //
      // Im KnxParameterViewer entsteht eine zusätzlicher Column für die Tabelle
      columnCount--;
      return columnCount;
    } else {
      if (panelMappingConfig.columns && typeof panelMappingConfig.columns === "function") {
        return panelMappingConfig.columns(pageIndex);
      }
      return panelMappingConfig.columns || 0;
    }
  }

  static applyAllFunctionNumberToAllCells(panel: Button, panelMappingConfig: PanelConfig, pageIndex: number) {
    const panelCells = panel.panelCells;
    if (panelCells) {
      const rowCount = PanelUtils.getRows(panel, panelMappingConfig, pageIndex);
      const columnCount = PanelUtils.getColumns(panel, panelMappingConfig, pageIndex);

      if (rowCount && columnCount) {
        for (let rI = 0; rI < rowCount; rI++) {
          for (let cI = 0; cI < columnCount; cI++) {
            let cell = panelCells.cells.find((c) => c.column === cI && c.row === rI && c.page === pageIndex);
            if (!cell) {
              cell = {
                functionTag: "",
                page: pageIndex,
                column: cI,
                row: rI,
                connectedObjectID: "",
              };
            }

            PanelUtils.applyCellToFuctionNumberProperty(panel, panelMappingConfig, cell);
          }
        }
      } else {
        let pageLayout = PanelUtils.getPageLayout(panel, panelMappingConfig, pageIndex);
        if (pageLayout) {
          for (const cellLayout of pageLayout.cells) {
            let cell: PanelCell | undefined = panelCells.cells.find(
              (c) => c.idx === cellLayout.idx && c.page === pageIndex,
            );
            if (!cell) {
              cell = {
                functionTag: "",
                page: pageIndex,
                idx: cellLayout.idx,
                connectedObjectID: "",
              };
            }
            PanelUtils.applyCellToFuctionNumberProperty(panel, panelMappingConfig, cell);
          }
        }
      }
    }
  }

  //
  // if the user has removed of modifed a function
  // we need to remove the cells from the panelLayout that are referencing to the removed or modified function
  //
  static removeCellsWithoutFunction(panel: Button) {
    const cells = panel.panelCells?.cells;
    if (cells) {
      const deadCells = cells.filter((c) => {
        const functioForCell = panel.buttonChannels.findFirst((bc) => {
          //
          // the function we are looking for is the one that has the right tag (c.functionTag)
          // is connected to the consumer with the right id (c.connectedObjectID)
          //
          if (bc.tag === c.functionTag) {
            const firstConsumer = bc.getAllConnectedConsumers().find((consumer) => consumer.id === c.connectedObjectID);
            return firstConsumer != undefined;
          }
          return false;
        });

        //
        // if the function for the cell is not found, the cell is dead
        //
        return functioForCell == undefined;
      });

      //
      // remove the dead cells from the panelLayout
      //
      for (const deadCell of deadCells) {
        cells.splice(cells.indexOf(deadCell), 1);
      }
    }
  }
  static getNumberOfPages(panel: Device, panelMappingConfig: PanelConfig | undefined) {
    const pageNumberId = panelMappingConfig?.pageNumberId || "";
    if (pageNumberId) {
      const n = parseInt(panel.knxInstanceData.getValue(pageNumberId, "") || "1");
      return n;
    } else {
      return 1;
    }
  }

  static setNumberOfPages(panel: Device, panelMappingConfig: PanelConfig | undefined, n: number) {
    const pageNumberId = panelMappingConfig?.pageNumberId || "";
    if (pageNumberId) {
      panel.knxInstanceData.setValue(ValueLevels.interactive, n.toString(), pageNumberId, "", "");
      panel.raiseOnChange("pageNumber");
    }
  }

  static getOrientation(panel: Device, panelMappingConfig: PanelConfig | undefined) {
    const orientationId = panelMappingConfig?.orientation?.id || "";
    if (orientationId) {
      const v = panel.knxInstanceData.getValue(orientationId, "") || "H";
      if (v === String(panelMappingConfig?.orientation?.values.horizontal)) {
        return "H";
      } else if (v === String(panelMappingConfig?.orientation?.values.vertical)) {
        return "V";
      }
    }
    return "H";
  }

  static setOrientation(panel: Device, panelMappingConfig: PanelConfig | undefined, orientation: string) {
    const orientationId = panelMappingConfig?.orientation?.id || "";
    const orientationValue_Horizontal = panelMappingConfig?.orientation?.values.horizontal || 0;
    const orientationValue_Vertical = panelMappingConfig?.orientation?.values.vertical || 0;
    if (orientation === "H") {
      panel.knxInstanceData.setValue(
        ValueLevels.interactive,
        String(orientationValue_Horizontal),
        orientationId,
        "",
        "",
      );
    } else if (orientation === "V") {
      panel.knxInstanceData.setValue(ValueLevels.interactive, String(orientationValue_Vertical), orientationId, "", "");
    }
    panel.raiseOnChange("orientation");
  }

  static layoutIsVertial(panel: Button, panelMappingConfig: PanelConfig): boolean {
    const orientation = PanelUtils.getOrientation(panel, panelMappingConfig);
    return orientation === "V";
  }

  static findIndexForCell(panel: Button, pageLayout: PannelCellLayouts, cell: PanelCell): string | undefined {
    const panelLayout = panel.panelCells;
    if (panelLayout?.cells) {
      return pageLayout.find((field) => field.x === cell.column && field.y === cell.row)?.idx;
    }
    return undefined;
  }

  static markAsManagedByEstada(panel: Button, id: string, instanceId: string) {
    panel.knxInstanceData.setExtendedData(
      {
        mbe: {
          isSet: false,
        },
      },
      id,
      instanceId,
    );
  }

  static getPageLayout(panel: Button, panelMappingConfig: PanelConfig, pageIndex: number): PanelLayout | undefined {
    let pageLayout: PanelLayout | undefined;

    let pageLayouts = panelMappingConfig.horizontalPageLayouts;

    if (PanelUtils.layoutIsVertial(panel, panelMappingConfig)) {
      pageLayouts = panelMappingConfig.verticalPageLayouts;
    }

    if (pageLayouts) {
      const templateId = panelMappingConfig.templateId || "";
      const instanceId = panelMappingConfig.pageInstanceId?.(pageIndex);

      const templateNumber = parseInt(panel.knxInstanceData.getValue(templateId, instanceId) || "1");

      pageLayout = pageLayouts[templateNumber];
    }

    return pageLayout;
  }
}
